import React from 'react';

interface TermsOfServiceProps {
  onBack: () => void;
}

export const TermsOfService: React.FC<TermsOfServiceProps> = ({ onBack }) => {
  return (
    <div className="bg-slate-50 min-h-full font-sans">
      <nav className="bg-white border-b border-slate-100 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 cursor-pointer" onClick={onBack}>
                <img 
                    src="https://image2url.com/r2/default/images/1769369928623-3180bbac-a140-4e50-ac88-1cfcf218a72a.png" 
                    alt="LoveOnRead Logo" 
                    className="w-8 h-8 object-contain"
                />
                <span className="font-bold text-xl tracking-tight">
                    <span className="text-love">Love</span>
                    <span className="text-read">OnRead</span>
                </span>
            </div>
            <button 
                onClick={onBack}
                className="text-sm font-medium text-slate-600 hover:text-slate-900"
            >
                Back
            </button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-extrabold text-slate-900 mb-8">Terms of Service</h1>
        <div className="prose prose-slate max-w-none bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
            <p className="text-sm text-slate-500 mb-6">Last Updated: February 2026</p>

            <h3>1. Introduction</h3>
            <p>
                Welcome to LoveOnRead ("we," "our," or "us"). These Terms of Service constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you") and <strong>Chukwuemeka Okparaeke</strong> ("Operator"), doing business as LoveOnRead, concerning your access to and use of the LoveOnRead website and application.
            </p>
            <p>
                By creating an account, accessing, or using LoveOnRead ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service. 
            </p>
            <p>
                <strong>For iOS Users:</strong> You acknowledge that this agreement is between you and LoveOnRead only, and not with Apple. LoveOnRead is solely responsible for the App and its content. By using the App, you also agree to Apple’s standard <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noreferrer">Licensed Application End User License Agreement (EULA)</a>.
            </p>

            <h3>2. Description of Service & AI Disclaimer</h3>
            <p>
                LoveOnRead provides AI-powered simulation and coaching tools designed to help users practice communication skills.
            </p>
            <p>
                <strong>IMPORTANT DISCLAIMER:</strong> The App is for <strong>entertainment and educational purposes only</strong>. 
                <ul>
                    <li>The advice is generated by Artificial Intelligence (Gemini AI) and may occasionally be inaccurate or inappropriate.</li>
                    <li>This App is <strong>NOT</strong> a substitute for professional psychological advice, therapy, or relationship counseling.</li>
                    <li>We are not responsible for real-world relationship outcomes resulting from the use of our simulations.</li>
                </ul>
            </p>

            <h3>3. Subscriptions, Billing, and Refund Policy</h3>
            <p>LoveOnRead offers a "Pro" subscription that unlocks unlimited scenarios and features.</p>

            <h4>A. For Mobile App Users (iOS & Android)</h4>
            <ul>
                <li><strong>Payment:</strong> Payment will be charged to your iTunes Account (iOS) or Google Play Account (Android) at confirmation of purchase.</li>
                <li><strong>Auto-Renewal:</strong> Subscriptions automatically renew unless auto-renew is turned off at least 24-hours before the end of the current period.</li>
                <li><strong>Renewal Charges:</strong> Your account will be charged for renewal within 24-hours prior to the end of the current period at the cost of the chosen package (Monthly or Yearly).</li>
                <li><strong>Management:</strong> You can manage your subscription and turn off auto-renewal by going to your Account Settings on your device after purchase.</li>
                <li><strong>Refunds:</strong> Refunds for mobile subscriptions are handled exclusively by Apple or Google in accordance with their refund policies.</li>
            </ul>

            <h4>B. For Web Users</h4>
            <ul>
                <li><strong>Payment:</strong> Payments are processed securely via our Merchant of Record.</li>
                <li><strong>Cancellation:</strong> You may cancel your web subscription at any time via your Profile settings on the website. Access will continue until the end of your billing cycle.</li>
                <li><strong>Refund Policy:</strong> We offer a 7-day money-back guarantee for initial web subscription purchases if you are not satisfied with the service. To request a refund, please contact support at <a href="mailto:loveonreadai@gmail.com">loveonreadai@gmail.com</a> with your transaction details. After 7 days, refunds are generally not provided for partial billing periods, but exceptions may be made on a case-by-case basis for technical issues.</li>
            </ul>

            <h3>4. User Conduct</h3>
            <p>You agree not to use the App to:</p>
            <ul>
                <li>Generate abusive, threatening, hate speech, or illegal content via the AI prompts.</li>
                <li>Harass, abuse, or harm another person using advice generated by the App.</li>
                <li>Reverse engineer the AI models or application code.</li>
            </ul>
            <p>We reserve the right to terminate accounts that violate these guidelines without refund.</p>

            <h3>5. Limitation of Liability</h3>
            <p>
                To the fullest extent permitted by law, LoveOnRead and Chukwuemeka Okparaeke shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly.
            </p>

            <h3>6. Changes to Terms</h3>
            <p>
                We may modify these Terms at any time. We will provide notice of significant changes by posting the new Terms on this page. Your continued use of the App after any such change constitutes your acceptance of the new Terms.
            </p>

            <h3>7. Contact Us</h3>
            <p>
                For any questions regarding these Terms, please contact us at <a href="mailto:loveonreadai@gmail.com">loveonreadai@gmail.com</a>.
            </p>
        </div>
      </main>
    </div>
  );
};